import {
  onAuthStateChanged as _onAuthStateChanged,
  User,
  NextFn,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import {
  signInWithGoogle as sharedSignInWithGoogle,
  signInWithEmail as sharedSignInWithEmail,
  signOut as sharedSignOut,
} from "shared/lib/firebase/auth";

import { auth } from "@/lib/firebase/clientApp";

export function onAuthStateChanged(cb: NextFn<User | null>) {
  return _onAuthStateChanged(auth, cb);
}

// Wrap shared functions with web's auth instance
export async function signInWithGoogle() {
  return sharedSignInWithGoogle(auth, "first.last@ohc400.org");
}

export async function signInWithEmail(email: string, password: string) {
  return sharedSignInWithEmail(auth, email, password);
}

export async function signOut() {
  return sharedSignOut(auth);
}

// ============================================================================
// Email Magic Link Authentication
// ============================================================================

/**
 * Send a magic link email to the user
 * @param email - The email address to send the magic link to
 * @param groupCode - Optional group code to include in the redirect URL for group-specific join flow
 */
export async function sendMagicLinkEmail(email: string, groupCode?: string) {
  try {
    const baseUrl = `${window.location.origin}/login?email=${encodeURIComponent(email)}`;
    const url = groupCode
      ? `${baseUrl}&join=${encodeURIComponent(groupCode)}`
      : baseUrl;
    const actionCodeSettings = {
      url,
      handleCodeInApp: true,
    };

    await sendSignInLinkToEmail(auth, email, actionCodeSettings);

    // Store email in localStorage for later use when completing sign-in
    if (typeof window !== "undefined") {
      window.localStorage.setItem("emailForSignIn", email);
      // Also store groupCode if provided
      if (groupCode) {
        window.localStorage.setItem("groupCodeForJoin", groupCode);
      }
    }
  } catch (error) {
    console.error("Error sending magic link email", error);
    throw error;
  }
}

/**
 * Check if the current URL contains a magic link
 */
export function checkMagicLinkInUrl(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return isSignInWithEmailLink(auth, window.location.href);
}

/**
 * Complete sign-in with a magic link
 * @param email - The email address (can be retrieved from localStorage if not provided)
 * @param emailLink - The full URL containing the magic link (defaults to window.location.href)
 */
export async function signInWithMagicLink(email?: string, emailLink?: string) {
  try {
    // Get email from localStorage if not provided
    let emailToUse = email;
    if (!emailToUse && typeof window !== "undefined") {
      emailToUse = window.localStorage.getItem("emailForSignIn") || undefined;
      if (!emailToUse) {
        // Prompt user for email if not available
        emailToUse =
          window.prompt("Please provide your email for confirmation") ||
          undefined;
      }
    }

    if (!emailToUse) {
      throw new Error("Email is required to complete sign-in");
    }

    // Use current URL if emailLink not provided
    const linkToUse =
      emailLink || (typeof window !== "undefined" ? window.location.href : "");

    const credential = await signInWithEmailLink(auth, emailToUse, linkToUse);

    // Clear email from localStorage after successful sign-in
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("emailForSignIn");
    }

    // Get ID token and send to /api/login (handled by next-firebase-auth-edge)
    const idToken = await credential.user.getIdToken();
    await fetch("/api/login", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return credential;
  } catch (error) {
    console.error("Error signing in with magic link", error);
    throw error;
  }
}

// ============================================================================
// SMS Magic Link Authentication
// ============================================================================

let recaptchaVerifier: RecaptchaVerifier | null = null;

/**
 * Initialize reCAPTCHA verifier for phone authentication
 * @param containerId - The ID of the DOM element to render reCAPTCHA (optional, uses invisible if not provided)
 * @returns The RecaptchaVerifier instance
 */
export function initializeRecaptcha(containerId?: string): RecaptchaVerifier {
  // Clean up existing verifier if reinitializing
  if (recaptchaVerifier) {
    recaptchaVerifier.clear();
    recaptchaVerifier = null;
  }

  recaptchaVerifier = new RecaptchaVerifier(
    auth,
    containerId || "recaptcha-container",
    {
      size: containerId ? "normal" : "invisible",
      callback: () => {
        // reCAPTCHA solved - this is handled automatically
        console.log("reCAPTCHA verified");
      },
      "expired-callback": () => {
        console.log("reCAPTCHA expired");
      },
      "error-callback": (error: Error) => {
        console.error("reCAPTCHA error:", error);
      },
    },
  );

  return recaptchaVerifier;
}

/**
 * Clean up reCAPTCHA verifier
 */
export function cleanupRecaptcha() {
  if (recaptchaVerifier) {
    recaptchaVerifier.clear();
    recaptchaVerifier = null;
  }
}

/**
 * Send a magic link SMS to the phone number
 * @param phoneNumber - The phone number in E.164 format (e.g., +1234567890)
 * @param groupCode - Optional group code to include in localStorage for group-specific join flow
 * @returns The verification ID (confirmation result)
 */
export async function sendMagicLinkSMS(
  phoneNumber: string,
  groupCode?: string,
): Promise<{ verificationId: string; confirmationResult: unknown }> {
  try {
    if (!recaptchaVerifier) {
      throw new Error(
        "RecaptchaVerifier not initialized. Call initializeRecaptcha() first.",
      );
    }

    // Store groupCode in localStorage for later use (SMS auth doesn't support custom redirect URLs)
    if (groupCode && typeof window !== "undefined") {
      window.localStorage.setItem("groupCodeForJoin", groupCode);
    }

    // Ensure phone number is in E.164 format
    const formattedPhone = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+1${phoneNumber.replace(/\D/g, "")}`;

    const confirmationResult = await signInWithPhoneNumber(
      auth,
      formattedPhone,
      recaptchaVerifier,
    );

    return {
      verificationId: confirmationResult.verificationId,
      confirmationResult,
    };
  } catch (error) {
    console.error("Error sending magic link SMS", error);
    throw error;
  }
}

/**
 * Verify SMS code and complete sign-in
 * @param verificationId - The verification ID from sendMagicLinkSMS
 * @param code - The 6-digit code sent via SMS
 */
export async function verifySMSCode(verificationId: string, code: string) {
  try {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const result = await signInWithCredential(auth, credential);

    // Clean up reCAPTCHA after successful verification
    cleanupRecaptcha();

    // Get ID token and send to /api/login (handled by next-firebase-auth-edge)
    const idToken = await result.user.getIdToken();
    await fetch("/api/login", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return result;
  } catch (error) {
    console.error("Error verifying SMS code", error);
    throw error;
  }
}
